import { usePanelResults } from '../../context/PanelResultsContext';
// The app as one conversation with a workspace beside it (founder, 2026-09-27:
// "the website feel should be like Claude … a chatbot for the entire job
// search, and on the right everything else"; the approved design canvas).
//
//   rail (NavigationPanel, outside) | the chat | the panel: the page for the URL
//
// The chat is mounted once, here, at the same place in the tree for every
// page in the shell, so typed words, the scroll position and a reply still
// being written survive every change of panel. Each page used to mount its
// own copy of the chat, in its own card, drawn its own way. The panel shows
// the page for the current URL inside its own loading and error boundaries:
// a slow or broken page never blanks the conversation.
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AntigravitySidebar from '../AntigravitySidebar/AntigravitySidebar';
import ErrorBoundary from '../ErrorBoundary';
import { ShellContext } from './ShellContext';
import { HOME_PATH, panelFor, tabsFor } from './panelRoutes';
import ApplicationsKeeper from './ApplicationsKeeper';
import { ApplicationsBridge } from './ApplicationsBridge';
import { OPEN_SETUP, SHOW_CHAT } from '../../services/aboutYouEvents';
import './ChatShell.css';

// The panel's width, as this person last left it. A per-viewer convenience:
// the page works the same when storage is blocked.
const WIDTH_KEY = 'aa.shell.panelWidth';
const readWidth = () => {
  try { const w = Number(window.localStorage.getItem(WIDTH_KEY)); return w > 0 ? w : null; } catch { return null; }
};
const saveWidth = (w) => {
  try { window.localStorage.setItem(WIDTH_KEY, String(Math.round(w))); } catch { /* storage blocked */ }
};
const MIN_PANEL = 560;
const MIN_CHAT = 380;

export default function ChatShell({ children, fallback = null }) {
  const { pathname, search, key: locationKey } = useLocation();
  const navigate = useNavigate();
  const { panelCounts, choosePanel, startedBy } = usePanelResults();
  const panel = panelFor(pathname);
  // A page of its own (Chats, 2026-09-28): the whole width and no tabs. The
  // chat stays mounted out of sight, so a draft or a reply still being
  // written survives the visit.
  const page = Boolean(panel?.page);
  const beside = panel && !page;
  const tabs = tabsFor(pathname);
  const shellRef = useRef(null);
  const [chatActions, setChatActions] = useState({});
  const [panelWidth, setPanelWidth] = useState(readWidth);
  // Below 1200px there is room for one thing at a time: the page, with the
  // chat slid over it on request.
  const [chatOver, setChatOver] = useState(false);
  // Applications keeps running once opened (ApplicationsKeeper): drawn into
  // the panel's slot while open, into a hidden holder otherwise. It starts
  // too once the chat has started an application, so the reply can ask what
  // holds it up (ApplicationsBridge) with no panel open.
  const onApplications = panel?.path === '/job-agent';
  const chatStarted = (startedBy?.size || 0) > 0;
  const [keepApplications, setKeepApplications] = useState(false);
  useEffect(() => { if (onApplications || chatStarted) setKeepApplications(true); }, [onApplications, chatStarted]);
  const [slot, setSlot] = useState(null);
  const [holder, setHolder] = useState(null);

  // A different page in the panel shows the page first.
  useEffect(() => { setChatOver(false); }, [pathname, search, locationKey]);

  useEffect(() => {
    if (!chatOver) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setChatOver(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatOver]);

  // "Continue in chat" and "Write with AI" (About you) put work in the chat;
  // where the page covers the chat, the chat slides over it to show it.
  useEffect(() => {
    const show = () => { if (window.innerWidth < 1200) setChatOver(true); };
    window.addEventListener(OPEN_SETUP, show);
    window.addEventListener(SHOW_CHAT, show);
    return () => {
      window.removeEventListener(OPEN_SETUP, show);
      window.removeEventListener(SHOW_CHAT, show);
    };
  }, []);

  const clampWidth = useCallback((w) => {
    const box = shellRef.current?.getBoundingClientRect();
    const most = box ? box.width - MIN_CHAT : w;
    return Math.round(Math.max(MIN_PANEL, Math.min(w, most)));
  }, []);

  // Drag the handle between the chat and the panel.
  const startResize = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    const box = shellRef.current?.getBoundingClientRect();
    if (!box) return;
    let last = null;
    const move = (ev) => { last = clampWidth(box.right - ev.clientX - 12); setPanelWidth(last); };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      document.body.classList.remove('shell-resizing');
      if (last) saveWidth(last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    document.body.classList.add('shell-resizing');
  };
  // And with the keyboard: the arrows move the edge.
  const resizeByKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const box = shellRef.current?.getBoundingClientRect();
    const now = panelWidth || (box ? box.width * 0.55 : MIN_PANEL);
    const next = clampWidth(now + (e.key === 'ArrowLeft' ? 40 : -40));
    setPanelWidth(next);
    saveWidth(next);
  };

  const context = useMemo(() => ({ inShell: true, panel, setChatActions }), [panel]);

  return (
    <ShellContext.Provider value={context}>
      <ApplicationsBridge>
        <div
          ref={shellRef}
          className={`shell${beside ? ' has-panel' : ''}${page ? ' is-page' : ''}${chatOver ? ' is-chat-over' : ''}`}
          data-no-scroll=""
          style={beside && panelWidth ? { '--shell-panel-w': `${panelWidth}px` } : undefined}
        >
          <section className="shell-chat" aria-label="Chat" aria-hidden={page || undefined}>
            {beside && (
              <button type="button" className="shell-chat-hide" onClick={() => setChatOver(false)} aria-label={`Back to ${panel.label}`} title={`Back to ${panel.label}`}>
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            )}
            <AntigravitySidebar embedded layout="full" {...chatActions} />
          </section>

          {beside && (
            <div
              className="shell-resize"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the panel"
              tabIndex={0}
              onPointerDown={startResize}
              onKeyDown={resizeByKey}
              onDoubleClick={() => { setPanelWidth(null); try { window.localStorage.removeItem(WIDTH_KEY); } catch { /* blocked */ } }}
            >
              <span aria-hidden="true" />
            </div>
          )}

          {page && (
            <section className="shell-page" aria-label={panel.label}>
              <ErrorBoundary key={panel.path}>
                <Suspense fallback={fallback}>{children}</Suspense>
              </ErrorBoundary>
            </section>
          )}

          {beside && (
            <section className="shell-panel" aria-label={panel.label}>
              <div className="shell-panel-bar">
                <nav className="shell-tabs" aria-label="Open beside the chat">
                  {tabs.map(t => (
                    <Link
                      key={t.path}
                      to={t.path}
                      onClick={() => choosePanel(t.label)}
                      className={`shell-tab${t.path === panel.path ? ' is-on' : ''}`}
                      aria-current={t.path === panel.path ? 'page' : undefined}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">{t.icon}</span>
                      {t.label}
                      {panelCounts[t.label] > 0 && <span className="shell-tab-count">{panelCounts[t.label]}</span>}
                    </Link>
                  ))}
                </nav>
                <div className="shell-panel-acts">
                  <button type="button" className="shell-chat-open" onClick={() => setChatOver(true)}>
                    <span className="material-symbols-rounded" aria-hidden="true">chat</span>
                    Chat
                  </button>
                  <button type="button" className="shell-close" onClick={() => navigate(HOME_PATH)} aria-label="Close the panel" title="Close">
                    <span className="material-symbols-rounded" aria-hidden="true">close</span>
                  </button>
                </div>
              </div>
              <div className="shell-panel-body">
                {onApplications ? <div className="shell-slot" ref={setSlot} /> : (
                  <ErrorBoundary key={panel.path}>
                    <Suspense fallback={fallback}>{children}</Suspense>
                  </ErrorBoundary>
                )}
              </div>
            </section>
          )}
          <div hidden ref={setHolder} />
          {keepApplications && <ApplicationsKeeper active={onApplications} host={onApplications ? slot : null} holder={holder} />}
        </div>
      </ApplicationsBridge>
    </ShellContext.Provider>
  );
}
